/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react'
import {
  DropzoneInputProps,
  DropzoneRootProps,
  useDropzone,
} from 'react-dropzone'
import { StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native'
import { Text, Title } from 'react-native-paper'
import { Trans, useTranslation } from 'react-i18next'
import { helpers, theme } from '@linqpal/common-frontend'
import { routes } from '@linqpal/models'
import {
  ArrowBlue,
  BankIcon,
  IconClose,
  IconComplete,
  IconFileUpload,
  IconInProgress,
  IconJPG,
  IconRefresh,
} from '../../assets/icons'
import { SButton } from '../../ui/atoms'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { ListItem } from '../../ui/atoms/builder-2.0/ListItem'
import axios from 'axios'

const { commonColors } = theme
const { composeStyle } = helpers

const styles = StyleSheet.create({
  dropzone: {
    marginBottom: 16,
    height: 70,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderColor: '#9BAEBC',
    borderWidth: 1,
    borderRadius: 4,
    borderStyle: 'dashed',
  },
  wrapper: {
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderColor: '#9BAEBC',
    borderWidth: 1,
    borderRadius: 4,
    flexDirection: 'column',
  },
  label: {
    color: commonColors.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 24,
  },
})

type Props = {
  label: string
  subLabel: string
  listTitle?: string
  disabled: boolean
  accept: Array<string>
  maxFiles?: number
  maxSize?: number
  model: any
  name: string
  documentName: string
  downloadFile?: any
  uploadCheck?: boolean
}

export default function useFileInput({
  label,
  subLabel,
  listTitle = '',
  disabled,
  accept = [],
  maxFiles = 1,
  maxSize = 2 * 1024 * 1024,
  model,
  name,
  documentName,
  downloadFile = null,
  uploadCheck = false,
}: Props) {
  const { t } = useTranslation('global')
  const dropzoneOptions = useMemo(
    () => ({
      label,
      subLabel,
      listTitle,
      accept,
      maxFiles,
      disabled,
      maxSize,
      multiple: false,
    }),
    [label, subLabel, listTitle, accept, maxFiles, disabled, maxSize],
  )
  const {
    open,
    isFocused,
    acceptedFiles,
    getRootProps,
    getInputProps,
    fileRejections,
  } = useDropzone(dropzoneOptions)

  const [file, setFile] = useState<File | null>(model[name])
  const [fileName, setFileName] = useState('')
  const [status, setStatus] = useState('INPROGRESS')
  const rootProps = useMemo<DropzoneRootProps>(
    () => ({ ...getRootProps() }),
    [getRootProps],
  )
  const inputProps = useMemo<DropzoneInputProps>(
    () => ({ ...getInputProps() }),
    [getInputProps],
  )

  useEffect(() => {
    if (acceptedFiles.length < 1) {
      return
    }
    setFile(acceptedFiles[0])
    routes.file
      .upload([acceptedFiles[0].name])
      .then(({ results }) => {
        const uploads: Array<Promise<any>> = []
        for (const f of acceptedFiles) {
          const r = results.find((res) => {
            return res.file === f.name
          })
          if (r.url) {
            const uploader = axios.create({
              url: r.url,
              method: 'PUT',
              data: f,
            })
            uploads.push(
              uploader
                .put(r.url, f)
                .then((e) => (e.status === 200 ? r : { result: 'failed' })),
            )
          }
        }
        return Promise.all(uploads)
      })
      .then((response) => {
        if (response[0]?.result === 'ok') {
          model.setValue(name, response[0].key)
          if (documentName) {
            model.setValue(documentName, response[0].file)
          }
          setFileName(response[0].file)
          setStatus('UPLOADED')
        } else {
          setStatus('RETRY')
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedFiles])

  useEffect(() => {
    if (!model[name]) {
      setFile(null)
      setStatus('INPROGRESS')
      acceptedFiles.splice(0, acceptedFiles.length)
      fileRejections.splice(0, fileRejections.length)
    } else {
      setFile(model[name])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, name])

  const downloadAndClose = () => {
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        {typeof file === 'string' && downloadFile && (
          <TouchableOpacity onPress={() => downloadFile()}>
            <MaterialCommunityIcons name="cloud-download-outline" size={18} />
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={{ marginLeft: 16 }}
          onPress={() => {
            model.setValue(name, '')
            if (documentName) {
              model.setValue(documentName, '')
            }
            setFile(null)
            setStatus('INPROGRESS')
          }}
        >
          <IconClose fontSize="12" />
        </TouchableOpacity>
      </View>
    )
  }

  const style = uploadCheck
    ? {}
    : composeStyle(
        styles.wrapper,
        isFocused ? { borderColor: commonColors.accent } : {},
      )

  const FileInput = (
    <View style={style}>
      {!file &&
        (uploadCheck ? (
          <View {...(rootProps as ViewStyle)}>
            <input {...inputProps} />
            <ListItem
              onPress={() => open()}
              disabled={disabled}
              icon={<BankIcon />}
              endIcon={<ArrowBlue />}
              style={{
                minHeight: 48,
                backgroundColor: '#F5F7F8',
                paddingHorizontal: 15,
              }}
              otherStyles={{
                smallTitle: { marginBottom: 0 },
                title: { marginBottom: 0, marginTop: 3 },
                frontIcon: { marginVertical: 8 },
                endIcon: { marginTop: 2 },
              }}
              title={
                <Trans i18nKey="upload-voided-check" t={t}>
                  <Text>Upload a </Text>
                  <Text style={{ color: '#00A0F3', fontWeight: '600' }}>
                    voided check
                  </Text>
                </Trans>
              }
            />
          </View>
        ) : (
          <>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: 24,
              }}
            >
              <View>
                {!!label && <Text style={styles.label}>{label}</Text>}
                {!!subLabel && (
                  <Text
                    style={composeStyle(styles.label, { fontWeight: '300' })}
                  >
                    {subLabel}
                  </Text>
                )}
              </View>
              <SButton onPress={open} disabled={disabled}>
                {t('upload')}
              </SButton>
            </View>
            <View {...(rootProps as ViewStyle)} style={styles.dropzone}>
              <input {...inputProps} />
              <IconFileUpload />
            </View>
          </>
        ))}
      {fileRejections.length > 0 &&
        fileRejections.map(({ file: f, errors }) => {
          return (
            <View key={f.name || 'stub key'}>
              <Text style={{ color: '#E91616', fontSize: 16, lineHeight: 24 }}>
                {errors.map((error) => {
                  if (error.code === 'file-too-large') {
                    return 'The file size should not exceed the maximum size. Please make the file shorter and try again!'
                  }
                  return error.message
                })}
              </Text>
            </View>
          )
        })}
      {!!file &&
        (uploadCheck ? (
          <View
            style={{
              opacity: disabled ? 0.3 : 1,
              height: 52,
              justifyContent: 'center',
              width: '100%',
              maxWidth: 700,
              borderWidth: 1,
              borderColor: '#E6EBEE',
              borderRadius: 12,
              marginBottom: 20,
              alignItems: 'center',
              overflow: 'hidden',
              backgroundColor: '#F5F7F8',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                paddingLeft: '12px',
                paddingRight: '20px',
                paddingBottom: '4px',
                paddingTop: '4px',
              }}
            >
              <Title>{listTitle}</Title>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <IconJPG fontSize="12" style={{ marginRight: 10 }} />
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: '500',
                      color: commonColors.primary,
                    }}
                  >
                    {typeof file !== 'string'
                      ? `${file?.name.slice(0, 5)}...${file?.name.slice(-9)}`
                      : model[documentName]
                      ? `${model[documentName].slice(0, 5)}...${model[
                          documentName
                        ].slice(-9)}`
                      : 'Attachment'}
                  </Text>
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: '300',
                      color: commonColors.primary,
                    }}
                  >
                    {typeof file !== 'string'
                      ? `(${(file?.size / (1024 * 1024)).toFixed(2)}m)`
                      : ''}
                  </Text>
                  {typeof file !== 'string' && status === 'UPLOADED' && (
                    <IconComplete fontSize="12" style={{ marginLeft: 15 }} />
                  )}
                  {typeof file !== 'string' && status === 'INPROGRESS' && (
                    <IconInProgress fontSize="12" style={{ marginLeft: 15 }} />
                  )}
                  {typeof file !== 'string' && status === 'RETRY' && (
                    <IconRefresh fontSize="12" style={{ marginLeft: 15 }} />
                  )}
                </View>
                {downloadAndClose()}
              </View>
            </View>
          </View>
        ) : (
          <View>
            <Title>{listTitle}</Title>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <IconJPG fontSize="12" style={{ marginRight: 10 }} />
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: '500',
                    color: commonColors.primary,
                  }}
                >
                  {typeof file !== 'string'
                    ? file?.name
                    : model[documentName]
                    ? model[documentName]
                    : 'Attachment'}
                </Text>
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: '300',
                    color: commonColors.primary,
                  }}
                >
                  {typeof file !== 'string'
                    ? `(${(file?.size / (1024 * 1024)).toFixed(2)}m)`
                    : ''}
                </Text>
                {typeof file !== 'string' && status === 'UPLOADED' && (
                  <IconComplete fontSize="12" style={{ marginLeft: 10 }} />
                )}
                {typeof file !== 'string' && status === 'INPROGRESS' && (
                  <IconInProgress fontSize="12" style={{ marginLeft: 10 }} />
                )}
                {typeof file !== 'string' && status === 'RETRY' && (
                  <IconRefresh fontSize="12" style={{ marginLeft: 10 }} />
                )}
              </View>
              {downloadAndClose()}
            </View>
          </View>
        ))}
    </View>
  )

  return { FileInput, fileName }
}
